import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardKpiCard } from "@/components/dashboard/DashboardKpiCard";
import { DashboardNav } from "@/components/dashboard/DashboardNav";
import { FinancialPeriodFilterBar } from "@/components/dashboard/FinancialPeriodFilterBar";
import { MonthlySalesChart } from "@/components/dashboard/MonthlySalesChart";
import { RevenueSplitChart } from "@/components/dashboard/RevenueSplitChart";
import { SalesTrendChart } from "@/components/dashboard/SalesTrendChart";
import { TreasuryFlowChart } from "@/components/dashboard/TreasuryFlowChart";
import { PageHeader } from "@/components/PageHeader";
import { buildFinancialDashboardAnalytics } from "@/core/analytics/financial-analytics";
import {
  defaultFinancialPeriodFilter,
  type FinancialPeriodFilter,
} from "@/core/analytics/financial-period";
import { formatMoney } from "@/core/pharmacy/format";
import { routes } from "@/router/all_routes";

function IconRevenue() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconTreasury() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="2" y="6" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M2 10h20M6 14h.01M10 14h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconOutstanding() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 8v4l3 3M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconInflow() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconOutflow() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconRate() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3 3v18h18M7 16l4-4 4 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function FinancialDashboardPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [period, setPeriod] = useState<FinancialPeriodFilter>(defaultFinancialPeriodFilter);

  useEffect(() => {
    const bump = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:billing-documents", bump);
    window.addEventListener("hospitol:treasury", bump);
    window.addEventListener("storage", bump);
    return () => {
      window.removeEventListener("hospitol:billing-documents", bump);
      window.removeEventListener("hospitol:treasury", bump);
      window.removeEventListener("storage", bump);
    };
  }, []);

  const analytics = useMemo(() => {
    void refreshKey;
    return buildFinancialDashboardAnalytics({ period });
  }, [refreshKey, period]);

  const revenueTrendDaily = analytics.revenueDailyTrend.map((d) => ({
    label: d.label,
    date: d.date,
    revenue: d.revenue,
    invoices: d.documents,
  }));

  const isMonthlyTrend = analytics.trendGranularity === "month";

  return (
    <div className="ph-dashboard ph-dashboard--finance">
      <PageHeader
        title="Santé financière"
        description="Revenus, trésorerie, créances et répartition par service — consolidé facturation + ventes comptoir."
      />

      <section className="ph-dashboard-hero ph-dashboard-hero--compact ph-dashboard-hero--violet mb-4">
        <div className="ph-dashboard-hero-content">
          <p className="ph-dashboard-hero-kicker">Tableau de bord finance — ITaaS Hospitol</p>
          <p className="ph-dashboard-hero-date mb-0">{analytics.periodLabel}</p>
        </div>
        <div className="ph-dashboard-hero-actions">
          <DashboardNav />
        </div>
      </section>

      <FinancialPeriodFilterBar
        value={period}
        onChange={setPeriod}
        yearOptions={analytics.availableYears}
      />

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="CA période"
            value={formatMoney(analytics.revenuePeriod)}
            hint="Factures payées + ventes comptoir"
            icon={<IconRevenue />}
            accent="violet"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="Encaissé facturation"
            value={formatMoney(analytics.paidPeriod)}
            hint={`Facturé : ${formatMoney(analytics.issuedPeriod)}`}
            icon={<IconRevenue />}
            accent="teal"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="Entrées trésorerie"
            value={formatMoney(analytics.inflowsPeriod)}
            hint="Crédits sur la période"
            icon={<IconInflow />}
            accent="emerald"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="Sorties trésorerie"
            value={formatMoney(analytics.outflowsPeriod)}
            hint="Débits sur la période"
            icon={<IconOutflow />}
            accent="amber"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="Solde trésorerie"
            value={formatMoney(analytics.treasuryBalance)}
            hint="Instantané — tous comptes actifs"
            icon={<IconTreasury />}
            accent="teal"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="Créances ouvertes"
            value={formatMoney(analytics.outstandingAmount)}
            hint={
              analytics.collectionRatePct !== null
                ? `Taux encaissement période : ${analytics.collectionRatePct} %`
                : "Proforma, émises, en caisse"
            }
            icon={<IconOutstanding />}
            accent="amber"
          />
        </div>
      </div>

      <div className="row g-3 mb-4 ph-split-workspace">
        <div className="col-lg-7">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3 ph-split-detail-head">
              <div>
                <h2 className="h5 mb-1">
                  Revenus — {isMonthlyTrend ? "par mois" : "par jour"}
                </h2>
                <p className="text-muted small mb-0">{analytics.periodLabel}</p>
              </div>
              <Link to={routes.billingInvoices} className="btn btn-sm btn-outline-primary">
                Détail recettes
              </Link>
            </div>
            {isMonthlyTrend ? (
              <MonthlySalesChart data={analytics.revenueMonthlyTrend} />
            ) : (
              <SalesTrendChart data={revenueTrendDaily} />
            )}
          </div>
        </div>
        <div className="col-lg-5">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <h2 className="h5 mb-1">
              Trésorerie — entrées / sorties
            </h2>
            <p className="text-muted small mb-3">
              {isMonthlyTrend ? "Agrégation mensuelle" : "Mouvements journaliers"}
            </p>
            <TreasuryFlowChart
              data={isMonthlyTrend ? analytics.treasuryMonthlyTrend : analytics.treasuryDailyTrend}
            />
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4 ph-split-workspace">
        <div className="col-lg-6">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3 ph-split-detail-head">
              <div>
                <h2 className="h5 mb-1">Taux d&apos;encaissement</h2>
                <p className="text-muted small mb-0">Factures payées / facturées sur la période</p>
              </div>
              <Link to={routes.billingCashier} className="btn btn-sm btn-outline-primary">
                Caisse
              </Link>
            </div>
            <DashboardKpiCard
              label="Encaissement"
              value={
                analytics.collectionRatePct !== null
                  ? `${analytics.collectionRatePct} %`
                  : "—"
              }
              hint={`${formatMoney(analytics.paidPeriod)} encaissés · ${formatMoney(analytics.issuedPeriod)} facturés`}
              icon={<IconRate />}
              accent="violet"
            />
          </div>
        </div>
        <div className="col-lg-6">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <h2 className="h5 mb-1">Synthèse période</h2>
            <p className="text-muted small mb-3">{analytics.periodLabel}</p>
            <ul className="list-unstyled small mb-0 d-flex flex-column gap-2">
              <li className="d-flex justify-content-between">
                <span className="text-muted">Chiffre d&apos;affaires</span>
                <strong>{formatMoney(analytics.revenuePeriod)}</strong>
              </li>
              <li className="d-flex justify-content-between">
                <span className="text-muted">Entrées trésorerie</span>
                <strong className="text-success">{formatMoney(analytics.inflowsPeriod)}</strong>
              </li>
              <li className="d-flex justify-content-between">
                <span className="text-muted">Sorties trésorerie</span>
                <strong className="text-warning">{formatMoney(analytics.outflowsPeriod)}</strong>
              </li>
              <li className="d-flex justify-content-between border-top pt-2">
                <span className="text-muted">Solde net période</span>
                <strong>{formatMoney(analytics.inflowsPeriod - analytics.outflowsPeriod)}</strong>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-4">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <h2 className="h5 mb-1">Types de revenus</h2>
            <p className="text-muted small mb-3">{analytics.periodLabel}</p>
            <RevenueSplitChart data={analytics.revenueBySource} showZeroValues />
          </div>
        </div>
        <div className="col-lg-4">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <h2 className="h5 mb-1">Revenus par service</h2>
            <p className="text-muted small mb-3">{analytics.periodLabel}</p>
            <RevenueSplitChart
              data={analytics.revenueByService}
              emptyLabel="Orientez et facturez des actes pour voir la répartition."
            />
          </div>
        </div>
        <div className="col-lg-4">
          <div className="ph-invoice-sheet ph-dash-card h-100">
            <h2 className="h5 mb-1">Revenus par payeur</h2>
            <p className="text-muted small mb-3">Patient, assureur, entreprise</p>
            <RevenueSplitChart data={analytics.revenueByPayer} />
          </div>
        </div>
      </div>
    </div>
  );
}
